import React, { useState, useEffect, useRef, useMemo } from 'react';
import { styled } from '@mui/system';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import {
    IconButton,
    Tooltip,
    TextField,
    Autocomplete,
    Popper
} from '@mui/material';

import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';

import { toast } from 'react-toastify';
import AssetService from '../../../Services/assets.service';
import CustomDatePicker from '../CustomDates/CustomDatePicker/CustomDatePicker';
import AutocompleteSelect from '../AutocompleteSelect/AutocompleteSelect';
import AddAssetGroup from '../../Assets/SubComponents/AddAssetGroup';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import EditAsset from '../../Assets/SubComponents/EditAsset';
import zIndex from '@mui/material/styles/zIndex';

// 1) Import your AddAssetGroup modal

// For rendering Autocomplete items with hover style
const StyledListItem = styled('li')({
    '&:hover': {
        backgroundColor: '#F5F5F6',
        cursor: 'pointer',
    },
});

const sortAssetsByNumber = (assets) =>
    [...assets].sort((a, b) => {
        const an = parseInt(a?.assetNumber, 10);
        const bn = parseInt(b?.assetNumber, 10);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return String(a?.assetNumber || '').localeCompare(String(b?.assetNumber || ''));
    });

const sortChooseAssetOptions = (options) =>
    [...options].sort((a, b) => {
        const an = parseInt(a?.asset?.assetNumber, 10);
        const bn = parseInt(b?.asset?.assetNumber, 10);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return String(a?.asset?.assetNumber || '').localeCompare(String(b?.asset?.assetNumber || ''));
    });

const isAssetNotSold = (asset) => !asset?.sellDate;

/** Same rule as legacy "ThatDontBelongToAnyTransactions" — no expense/journal link yet. */
const isAssetTransactionFree = (asset) => !asset?.expenseId && !asset?.journalEntryId;

const sameAssetId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
};

const assetHasDocumentLink = (asset) =>
    Boolean(
        asset?.expenseId ||
        asset?.journalEntryId ||
        asset?.ExpenseId ||
        asset?.JournalEntryId
    );

const getRelationChildIds = (rel) => {
    const rootId = rel.rootAssetId || rel.rootAsset?.id;
    const ids =
        Array.isArray(rel?.relatedAssetIds) && rel.relatedAssetIds.length > 0
            ? rel.relatedAssetIds
            : (rel?.relatedAssets || []).map((c) => c?.id).filter(Boolean);
    return ids.filter((id) => id && id !== rootId);
};

// Main container styling
const StyledContainer = styled('div')({
    position: 'absolute',
    top: '10%',
    left: '20%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    minHeight: '60vh',
    maxHeight: '85vh',
    overflowY: 'auto',
    minWidth: '700px',
    zIndex: 1301,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
    border: '1px solid #304fff',
});

/** Minimal inline form for creating a new asset, side-by-side fields. */
function AddAssetInline({
    onClose,
    caseId,
    client,
    token,
    type,
    itemId,
    setShowAddAsset,
    // We pass all existing assets so we can populate the related-assets multi-select
    assets = [],
    assetGroups,
    assetRelations = [],
    refreshRelations,
    fullItem,
    user,
    fetchEnums,
    setShowAddGroupModal,
    editedItemVat,
    /** When opening as "addition to existing asset", pre-select this asset as a related child */
    initialAdditionAsset = null
}) {
    const { t, i18n } = useTranslation();
    const nodeRef = useRef(null);
    const [direction, setDirection] = useState('rtl');

    const maxAssetNumber = assets.length > 0
        ? Math.max(...assets.map(asset => asset.assetNumber))
        : 0;

    // Single-select: the existing asset that this new asset is an addition to.
    // The picked asset becomes the ROOT of an AssetRelation, the new asset becomes its CHILD.
    const [selectedParentAsset, setSelectedParentAsset] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [newAsset, setNewAsset] = useState({
        name: fullItem.description,
        originalPrice: Math.floor(fullItem.amount - calculateVatAmount(fullItem.amount, editedItemVat ?? fullItem.vat, fullItem.documentDate)),
        depreciationPercentage: '',
        purchaseDate: fullItem.documentDate,
        assetNumber: String(maxAssetNumber + 1),
    });

    // When opened as "addition to existing asset", pre-select the addition target as the PARENT
    useEffect(() => {
        if (!initialAdditionAsset) return;
        setSelectedParentAsset(initialAdditionAsset);
        if (initialAdditionAsset.assetGroupId && assetGroups?.length) {
            const group = assetGroups.find(g => g.assetGroupId === initialAdditionAsset.assetGroupId);
            if (group) {
                setSelectedGroup(group);
                if (group.depreciationPercentage != null) {
                    setNewAsset((prev) => ({ ...prev, depreciationPercentage: group.depreciationPercentage }));
                }
            }
        }
    }, [initialAdditionAsset, assetGroups]);

    // Only show assets that can act as a ROOT — i.e. assets that are NOT already
    // a child of any existing AssetRelation. Such assets would be rejected by the
    // server (validation #6: a root cannot currently be a child).
    const baseEligibleParentAssets = useMemo(() => {
        if (!Array.isArray(assets)) return [];

        const childIdsLinkedElsewhere = new Set();
        (assetRelations || []).forEach((rel) => {
            const ids = Array.isArray(rel?.relatedAssetIds) && rel.relatedAssetIds.length > 0
                ? rel.relatedAssetIds
                : (rel?.relatedAssets || []).map((ra) => ra?.id).filter(Boolean);
            ids.forEach((id) => childIdsLinkedElsewhere.add(id));
        });

        return assets
            .filter((a) => a && a.id && !childIdsLinkedElsewhere.has(a.id))
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [assets, assetRelations]);

    const selectedAssetGroupId = selectedGroup?.assetGroupId || '';

    const eligibleParentAssets = useMemo(() => {
        if (!selectedAssetGroupId) return baseEligibleParentAssets;
        return baseEligibleParentAssets.filter((a) => a.assetGroupId === selectedAssetGroupId);
    }, [baseEligibleParentAssets, selectedAssetGroupId]);

    useEffect(() => {
        if (!selectedAssetGroupId || !selectedParentAsset) return;
        if (selectedParentAsset.assetGroupId !== selectedAssetGroupId) {
            setSelectedParentAsset(null);
        }
    }, [selectedAssetGroupId, selectedParentAsset]);

    useEffect(() => {
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);




    // Some textfield styling
    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    const handleChange = (field, value) => {
        setNewAsset((prev) => ({ ...prev, [field]: value }));
    };

    // 2) We'll hold a state for "Add Group" modal if we want to open it from this form, but
    //    typically "Add Group" is used in the ChooseAsset or a top-level place. 
    //    If you want it here too, define something like:
    // const [showAddGroupModal, setShowAddGroupModal] = useState(false);

    //    Then you’d do the same trick as shown below. For now, we’ll do it just for the group selection:

    const handleSelectAssetGroup = (group) => {
        if (!group) {
            setSelectedGroup(null);
            setNewAsset((prev) => ({
                ...prev,
                assetGroupId: '',
                depreciationPercentage: '',
            }));
            return;
        }

        // If user picked the "Add Group" item
        if (group.id === 'add_group_button') {
            if (typeof setShowAddGroupModal === 'function') {
                setShowAddGroupModal(true);
            }
            return;
        }

        setSelectedGroup(group);
        // Normal group
        setNewAsset((prev) => ({
            ...prev,
            assetGroupId: group.assetGroupId,
            depreciationPercentage: group.depreciationPercentage
        }));
    };



    // 3) Combine the real groups with a special "Add Group" object
    const assetGroupsWithAdd = [
        { id: 'add_group_button', name: t('AddGroup'), depreciationPercentage: '' },
        ...assetGroups,
    ];




    const isSaveDisabled = useMemo(() => {
        return (
            !newAsset.name?.trim() ||
            !newAsset.originalPrice || isNaN(newAsset.originalPrice) ||
            !newAsset.depreciationPercentage || isNaN(newAsset.depreciationPercentage) ||
            !newAsset.purchaseDate
        );
    }, [newAsset]);

    // Called when user clicks "Save" in the Add-Asset form
    const handleSaveClick = async () => {
        if (!newAsset.name?.trim() || !newAsset.originalPrice || isNaN(newAsset.originalPrice) ||
            !newAsset.depreciationPercentage || isNaN(newAsset.depreciationPercentage) ||
            !newAsset.purchaseDate) {
            toast.error(t('PleaseFillAllRequiredFieldsCorrectly'));
            return;
        }

        try {
            if (!newAsset.name) {
                toast.error(t('PleaseEnterAssetName'));
                return;
            }

            const assetData = {
                ...newAsset,
                caseId: client.caseId, // Assuming `client.caseId` is available
            };

            // 1) Create the new asset
            const createdAsset = await AssetService.CreateAsset(assetData, token);
            if (!createdAsset || !createdAsset.data.id) {
                toast.error(t('AssetCreationFailed'));
                return;
            }
            toast.success(t('AssetCreatedSuccessfully'));

            // 2) Then attach the document to the newly created asset
            await AssetService.AddDocumentToAsset(
                caseId,
                createdAsset.data.id,
                type,  // e.g. "Expense", "Income", or "JournalEntry"
                itemId,
                token
            );

            // 3) If the user picked a parent asset, save the AssetRelation
            //    with the PICKED asset as root and the newly-created asset as a CHILD.
            //    Merge with any existing children of that root so we don't drop them.
            if (selectedParentAsset?.id) {
                try {
                    const existingRelation = (assetRelations || []).find(
                        (r) => r?.rootAssetId === selectedParentAsset.id
                    );
                    const existingChildIds = Array.isArray(existingRelation?.relatedAssetIds)
                        ? existingRelation.relatedAssetIds
                        : (existingRelation?.relatedAssets || []).map((ra) => ra.id);
                    const mergedIds = Array.from(new Set([...(existingChildIds || []), createdAsset.data.id]));

                    const relationResp = await AssetService.SaveAssetRelation(
                        {
                            caseId: client.caseId,
                            rootAssetId: selectedParentAsset.id,
                            relatedAssetIds: mergedIds,
                        },
                        token
                    );
                    if (!relationResp?.success) {
                        toast.error(relationResp?.error || t('Failed to save asset relation'));
                    } else if (typeof refreshRelations === 'function') {
                        try { await refreshRelations(); } catch (_) { /* non-fatal */ }
                    }
                } catch (relErr) {
                    console.error('Error saving asset relation after creation:', relErr);
                    toast.error(relErr?.message || t('Failed to save asset relation'));
                }
            }

            // Refresh your data
            await fetchEnums(user, token, client);

            toast.success(t('DocumentAttachedSuccessfully'));
            onClose();
        } catch (error) {
            console.error('Error creating or attaching doc to new asset:', error);
            toast.error(error.message || t('AnErrorOccurred'));
        }
    };

    // A custom popper for the "Addition to asset" Autocomplete
    const StyledPopper = styled(Popper)(({ theme }) => ({
        '& .MuiPaper-root': {
            zIndex: 6000,
            borderRadius: '8px',
            background: '#fff',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
        },
    }));

    return (
        <div
            style={{
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px',
            }}
        >
            <h3 style={{ margin: 0 }}>{t('AddAsset')}</h3>

            {/* 1) Row => AssetName + OriginalPrice */}
            <div
                className="form-row-2"
                style={{ display: 'flex', gap: '20px', marginTop: '12px' }}
            >

                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('SerialNumber')}:</label>
                    <TextField
                        fullWidth
                        value={newAsset.assetNumber}
                        onChange={(e) => handleChange('assetNumber', e.target.value)}
                        variant="standard"
                        InputProps={inputPropsStyle}
                        inputProps={inputStyle}
                    />
                </div>


                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('ChooseGroup')}</label>
                    <Autocomplete
                        options={assetGroupsWithAdd}
                        getOptionLabel={(option) =>
                            option.id === 'add_group_button'
                                ? option.name // "AddGroup"
                                : `${option.name} - ${option.depreciationPercentage || ''}%`
                        }
                        value={selectedGroup || null}
                        onChange={(event, newVal) => handleSelectAssetGroup(newVal)}
                        isOptionEqualToValue={(option, val) => option.id === val.id}
                        noOptionsText={<strong>{t('NoOptions')}</strong>}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t('SelectAssetGroup')}
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: {
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: '#F5F5F6',
                                        border: '1px solid transparent',
                                        width: '100%',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                    },
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Tooltip
                                title={
                                    option.id === 'add_group_button'
                                        ? t('AddGroup')
                                        : t('')
                                }
                                placement="top"
                            >
                                <StyledListItem {...props}>
                                    {option.id === 'add_group_button'
                                        ? option.name
                                        : `${option.name} - ${option.depreciationPercentage || ''}%`}
                                </StyledListItem>
                            </Tooltip>
                        )}
                        PopperComponent={(props) => (
                            <StyledPopper
                                {...props}
                                style={{ ...props.style, direction, zIndex: 1305 }}
                            />
                        )}
                    />
                </div>



            </div>

            <div
                className="form-row-2"
                style={{ display: 'flex', gap: '20px', marginTop: '12px' }}
            >
                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('AssetName')}:</label>
                    <TextField
                        fullWidth
                        placeholder={t('AssetName')}
                        value={newAsset.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        variant="standard"
                        InputProps={inputPropsStyle}
                        inputProps={inputStyle}
                    />
                </div>

                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('DepreciationPercentage')}:</label>
                    <TextField
                        fullWidth
                        type="number"
                        value={newAsset.depreciationPercentage}
                        onChange={(e) => handleChange('depreciationPercentage', e.target.value)}
                        variant="standard"
                        InputProps={inputPropsStyle}
                        inputProps={inputStyle}
                    />
                </div>


            </div>

            {/* 2) Row => Depreciation + PurchaseDate */}
            <div
                className="form-row-2"
                style={{ display: 'flex', gap: '20px', marginTop: '12px' }}
            >
                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('OriginalPrice')}:</label>
                    <TextField
                        fullWidth
                        type="number"
                        value={newAsset.originalPrice}
                        onChange={(e) => handleChange('originalPrice', e.target.value)}
                        variant="standard"
                        InputProps={inputPropsStyle}
                        inputProps={inputStyle}
                    />
                </div>


                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('ChooseAssetIfAdditionToAsset')}</label>
                    <Autocomplete
                        options={eligibleParentAssets}
                        getOptionLabel={(option) =>
                            `${option.name} - ${option.assetNumber || ''}`
                        }
                        value={selectedParentAsset}
                        onChange={(event, newVal) => setSelectedParentAsset(newVal || null)}
                        isOptionEqualToValue={(option, val) => option?.id === val?.id}
                        noOptionsText={<strong>{t('NoOptions')}</strong>}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t('SelectAnAsset')}
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: {
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: '#F5F5F6',
                                        border: '1px solid transparent',
                                        width: '100%',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                    },
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <StyledListItem {...props}>
                                {`${option.name} - ${option.assetNumber || ''}`}
                            </StyledListItem>
                        )}
                        PopperComponent={(props) => (
                            <StyledPopper
                                {...props}
                                style={{ ...props.style, direction, zIndex: 1305 }}
                            />
                        )}
                    />
                </div>




            </div>

            {/* 3) Row => "Addition to asset" + serialNumber */}
            <div
                className="form-row-2"
                style={{ display: 'flex', gap: '20px', marginTop: '12px' }}
            >
                <div className="edit-form-row-item" style={{ flex: 1 }}>
                    <label>{t('PurchaseDate')}:</label>
                    <CustomDatePicker
                        date={newAsset.purchaseDate}
                        onDateChange={(date) => handleChange('purchaseDate', date.toISOString())}
                        height="48px"
                    />
                </div>

            </div>



            {/* Action buttons row */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '16px',
                    gap: '10px',
                }}
            >
                <button onClick={() => setShowAddAsset(false)} className="create-Invoice-selector-2-small">
                    <span>{t('Cancel')}</span>
                </button>

                <button
                    onClick={handleSaveClick}
                    className="create-Invoice-selector-2-small"
                    disabled={isSaveDisabled}
                    style={{
                        backgroundColor: isSaveDisabled ? '#ccc' : '#304FFF',
                        cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                    }}
                >
                    <span>{t('Save')}</span>
                    <SaveIcon fontSize="small" />
                </button>
            </div>

            {/* 5) If you want to show the "AddAssetGroup" modal FROM HERE, do it similarly: */}
            {/* 
            {showAddGroupModal && (
                <AddAssetGroup
                  open={showAddGroupModal}
                  onClose={() => setShowAddGroupModal(false)}
                  caseId={caseId}
                  token={token}
                  assetGroups={assetGroups}
                  refreshAssetGroups={() => {
                    fetchEnums(user, token, client);
                    setShowAddGroupModal(false);
                  }}
                />
            )}
            */}
        </div>
    );
}

function ChooseAsset({
    open = false,
    onClose,
    item,
    type,
    itemId,
    client,
    token,
    fullItem,
    saleEquipment = false,
    clickedAsset = null,
    user,
    fetchEnums,
    setShowAddGroupModal,
    assetGroups,
    editedItemVat,
    selectedAssetFromEdit = null,
    onDocumentLinked,
}) {



    const { t, i18n } = useTranslation();
    const nodeRef = useRef(null);

    const [fetchedAssets, setFetchedAssets] = useState([]);
    const [fetchedAssetRelations, setFetchedAssetRelations] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);

    const [showAddAsset, setShowAddAsset] = useState(false);
    // 6) Also track if we want to show the "Add Group" modal from here:

    const [loading, setLoading] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);


    const handleDelete = async (asset) => {
        if (!asset) {
            toast.error(t("Invalid asset details for deletion"));
            return;
        }
        try {
            const response = await AssetService.DeleteAsset(client.caseId, asset, token);
            if (response.success) {
                toast.success(t("AssetDeletedSuccessfully"));
                fetchEnums(user, token, client);
            } else {
                toast.error(t("Failed to delete asset"));
            }
            setEditModalOpen(false);
        } catch (error) {
            console.error(error);
            toast.error(t("An error occurred while deleting asset"));
        }
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
    };


    useEffect(() => {
        setIsSaveDisabled(!selectedAsset);
    }, [selectedAsset]);


    const [direction, setDirection] = useState('rtl');
    useEffect(() => {
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);

    // For styling the autocomplete popper
    const StyledPopper = styled(Popper)(({ theme }) => ({
        '& .MuiPaper-root': {
            borderRadius: '8px',
            background: '#fff',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
            zIndex: 6000,
        },
    }));

    const handleOpenAddAsset = () => {
        if (type === "Incomes") {
            toast.info(t('CannotAddAssetToIncome'));
            return;
        }
        setShowAddAsset(true);
    };

    const refreshAssetRelations = async () => {
        try {
            const relationsResp = await AssetService.GetAssetRelationsByCaseId(client?.caseId, token);
            if (relationsResp?.success) {
                setFetchedAssetRelations(relationsResp.data || []);
            }
        } catch (err) {
            console.error('Error fetching asset relations:', err);
        }
    };

    // On open, fetch assets & groups
    useEffect(() => {
        if (!open) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const [assetsResp] = await Promise.all([
                    AssetService.GetAllAssets(client?.caseId, token),
                ]);
                // Check if responses are valid
                if (assetsResp.success !== false) {
                    console.log('assetsResp.data', assetsResp.data);
                    setFetchedAssets(assetsResp.data);
                } else {
                    toast.error(t('FailedToFetchAssets'));
                }
                await refreshAssetRelations();
            } catch (err) {
                console.error(err);
                toast.error(t('fetchEnumsError'), { position: 'top-center' });
            }
            setLoading(false);
        };
        fetchData();
    }, [open, client, token, t]);

    // When user selects an asset that already has a document → treat as "addition" (תוספת)
    const isAdditionToExistingAsset =
        selectedAsset && !saleEquipment && assetHasDocumentLink(selectedAsset);

    const childAssetIdSet = useMemo(() => {
        const childSet = new Set();
        (fetchedAssetRelations || []).forEach((rel) => {
            const ids = Array.isArray(rel?.relatedAssetIds) && rel.relatedAssetIds.length > 0
                ? rel.relatedAssetIds
                : (rel?.relatedAssets || []).map((ra) => ra?.id).filter(Boolean);
            ids.forEach((id) => childSet.add(id));
        });
        return childSet;
    }, [fetchedAssetRelations]);

    const unsoldAssets = useMemo(
        () => (fetchedAssets || []).filter(isAssetNotSold),
        [fetchedAssets]
    );

    /** Legacy "ThatDontBelongToAnyTransactions" eligible pool (not sold + no document link). */
    const assetsNotBelongingToTransactions = useMemo(
        () => (fetchedAssets || []).filter((a) => isAssetNotSold(a) && isAssetTransactionFree(a)),
        [fetchedAssets]
    );

    const selectedAssetGroupId = selectedGroup?.assetGroupId || '';

    const passesAssetGroupFilter = (asset) =>
        !selectedAssetGroupId || asset.assetGroupId === selectedAssetGroupId;

    // Sale flow: unsold roots/standalone only
    const salePickableAssets = useMemo(() => {
        const list = unsoldAssets.filter(
            (a) => !childAssetIdSet.has(a.id) && !a.assetRelationId
        );
        return sortAssetsByNumber(
            selectedAssetGroupId ? list.filter(passesAssetGroupFilter) : list
        );
    }, [unsoldAssets, childAssetIdSet, selectedAssetGroupId]);

    // Addition flow (unchanged): all unsold when user re-opens picker with a linked asset selected
    const additionPickableAssets = useMemo(
        () =>
            sortAssetsByNumber(
                selectedAssetGroupId
                    ? unsoldAssets.filter(passesAssetGroupFilter)
                    : unsoldAssets
            ),
        [unsoldAssets, selectedAssetGroupId]
    );

    // !isAdditionToExistingAsset: transaction-free roots + eligible children (both selectable, grouped under root)
    const transactionFreeChooseOptions = useMemo(() => {
        const assetById = new Map((fetchedAssets || []).map((a) => [a.id, a]));
        const relationBlocks = [];
        const childIdsListed = new Set();
        const listedRootIds = new Set();

        const isEligible = (asset) =>
            assetsNotBelongingToTransactions.some((x) => x.id === asset.id) &&
            (!selectedAssetGroupId || asset.assetGroupId === selectedAssetGroupId);

        (fetchedAssetRelations || []).forEach((rel) => {
            const rootId = rel.rootAssetId || rel.rootAsset?.id;
            const root = rootId ? assetById.get(rootId) : null;

            const childOptions = getRelationChildIds(rel)
                .map((childId) => assetById.get(childId))
                .filter((child) => child && isEligible(child))
                .sort((a, b) => {
                    const an = parseInt(a?.assetNumber, 10);
                    const bn = parseInt(b?.assetNumber, 10);
                    if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
                    return String(a?.assetNumber || '').localeCompare(String(b?.assetNumber || ''));
                })
                .map((child) => {
                    childIdsListed.add(child.id);
                    return { asset: child, isChild: true, disabled: false };
                });

            const rootOption =
                root && isEligible(root)
                    ? [{ asset: root, isChild: false, disabled: false }]
                    : [];

            if (rootOption.length === 0 && childOptions.length === 0) return;

            if (root) listedRootIds.add(root.id);

            const sortKey = root?.assetNumber ?? childOptions[0]?.asset?.assetNumber;
            relationBlocks.push({
                rootNumber: parseInt(sortKey, 10) || 0,
                options: [...rootOption, ...childOptions],
            });
        });

        const standaloneOptions = (fetchedAssets || [])
            .filter((asset) => {
                if (childAssetIdSet.has(asset.id) || childIdsListed.has(asset.id)) return false;
                if (listedRootIds.has(asset.id)) return false;
                return isEligible(asset);
            })
            .map((asset) => ({ asset, isChild: false, disabled: false }));

        relationBlocks.sort((a, b) => {
            if (a.rootNumber !== b.rootNumber) return a.rootNumber - b.rootNumber;
            return 0;
        });

        return [
            ...relationBlocks.flatMap((block) => block.options),
            ...sortChooseAssetOptions(standaloneOptions),
        ];
    }, [fetchedAssets, fetchedAssetRelations, childAssetIdSet, assetsNotBelongingToTransactions, selectedAssetGroupId]);

    const autocompleteOptions = useMemo(() => {
        if (saleEquipment) {
            return salePickableAssets.map((asset) => ({
                asset,
                isChild: false,
                disabled: false,
            }));
        }
        if (isAdditionToExistingAsset) {
            return additionPickableAssets.map((asset) => ({
                asset,
                isChild: false,
                disabled: false,
            }));
        }
        return transactionFreeChooseOptions;
    }, [
        saleEquipment,
        isAdditionToExistingAsset,
        salePickableAssets,
        additionPickableAssets,
        transactionFreeChooseOptions,
    ]);

    const freeAssets = useMemo(
        () => autocompleteOptions.filter((o) => !o.disabled).map((o) => o.asset),
        [autocompleteOptions]
    );

    useEffect(() => {
        if (!selectedAsset || editModalOpen) return;
        const stillAllowed = freeAssets.some((a) => sameAssetId(a.id, selectedAsset.id));
        if (!stillAllowed) setSelectedAsset(null);
    }, [freeAssets, selectedAsset, editModalOpen]);

    const handleSelectAsset = (asset) => {
        if (!asset) {
            setSelectedAsset(null);
            return;
        }
        setSelectedAsset(asset);
        // Auto-select the asset's group in the filter
        if (asset?.assetGroupId && assetGroups?.length) {
            const group = assetGroups.find(g => g.assetGroupId === asset.assetGroupId);
            if (group) setSelectedGroup(group);
        }
        // When user selects an asset that already has a document → auto-open "new asset" form (addition flow)
        if (asset && !saleEquipment && assetHasDocumentLink(asset)) {
            setShowAddAsset(true);
        }
    };

    // EditModal / Document: open EditAsset after assets + relations are loaded (not during picker flow)
    useEffect(() => {
        if (!open || !clickedAsset || loading) return;

        const assetToEdit =
            (fetchedAssets || []).find((a) => sameAssetId(a.id, clickedAsset.id)) || clickedAsset;

        setShowAddAsset(false);
        setSelectedAsset(assetToEdit);
        if (assetToEdit?.assetGroupId && assetGroups?.length) {
            const group = assetGroups.find((g) => g.assetGroupId === assetToEdit.assetGroupId);
            if (group) setSelectedGroup(group);
        }
        setEditModalOpen(true);
    }, [open, clickedAsset, loading, assetGroups, fetchedAssets]);

    useEffect(() => {
        if (!open) {
            setEditModalOpen(false);
        }
    }, [open]);


    const handleSaveSelection = async () => {
        try {
            if (!selectedAsset) {
                toast.error(t('NoAssetSelected'));
                return;
            }
            if (!saleEquipment) {
                await AssetService.AddDocumentToAsset(
                    client.caseId,
                    selectedAsset.id,
                    type,
                    itemId,
                    token
                );
            } else {
                // Mark the asset as ended
                await AssetService.SetEndDate(
                    client.caseId,
                    selectedAsset.id,
                    type,
                    itemId,
                    fullItem.documentDate,
                    token,
                    fullItem.amount
                );
            }

            await fetchEnums(user, token, client);

            toast.success(t('DocumentAttachedSuccessfully'));
            if (typeof onDocumentLinked === 'function') {
                await onDocumentLinked();
            }
            onClose();
        } catch (error) {
            console.error('Error attaching doc to chosen asset');
            toast.error(t('AnErrorOccurred'));
        }
    };

    const handleSaveNewAsset = () => {
        setShowAddAsset(false);
        onClose();
    };

    if (!open) return null;

    return (
        <Draggable handle=".choose-asset-handle" nodeRef={nodeRef}>
            <StyledContainer ref={nodeRef}>
                {/* Draggable header */}
                <div
                    className="choose-asset-handle"
                    style={{
                        cursor: 'move',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',

                    }}
                >
                    <div></div>
                    {!editModalOpen && (
                        <div>{t('ChooseAssetTitle')}</div>
                    )}
                    <Tooltip title={t('Exit')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                </div>



                {!editModalOpen && (<>
                    <div style={{ marginBottom: '16px' }}>
                        <strong style={{ color: '#0f1042' }}>
                            {saleEquipment === false
                                ? t('YouChooseEquipmentVatPleaseSelectAssetToBeAddedFromAssetList')
                                : t('YouChooseSaleOfEquipmentPleaseSelectAssetToBeRemovedFromAssetList')}
                        </strong>
                    </div>

                    {loading && <div>{t('LoadingAssets')}...</div>}
                    {/* If not adding new, show row with 2 autocompletes */}
                    {!loading && !showAddAsset && (
                        <>
                            <div className="form-row-2" style={{ display: 'flex', gap: '20px' }}>
                                {/* Asset */}
                                <div className="edit-form-row-item" style={{ flex: 1 }}>
                                    <div
                                        className="edit-form-row-item-title"
                                        style={{ textAlign: 'right', width: '100%', marginBottom: '4px' }}
                                    >
                                        <span className="required-asterisk">*</span>
                                        {t('Asset') + " "}
                                        {!saleEquipment && !isAdditionToExistingAsset && (
                                            <span style={{ color: 'red', marginInlineStart: '8px' }}>
                                                {t('ThatDontBelongToAnyTransactions')}
                                            </span>
                                        )}
                                        {!saleEquipment && isAdditionToExistingAsset && (
                                            <span style={{ color: '#304FFF', marginInlineStart: '8px' }}>
                                                {t('ChooseAssetIfAdditionToAsset')}
                                            </span>
                                        )}
                                    </div>
                                    <Autocomplete
                                        options={autocompleteOptions}
                                        getOptionLabel={(option) =>
                                            option?.asset
                                                ? `${option.asset.name} - ${option.asset.assetNumber}`
                                                : ''
                                        }
                                        value={
                                            selectedAsset
                                                ? autocompleteOptions.find(
                                                      (o) => o.asset.id === selectedAsset.id
                                                  ) || null
                                                : null
                                        }
                                        onChange={(event, newVal) => handleSelectAsset(newVal?.asset ?? null)}
                                        isOptionEqualToValue={(option, val) =>
                                            option.asset.id === val.asset.id
                                        }
                                        noOptionsText={<strong>{t('NoOptions')}</strong>}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('SelectAnAsset')}
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
                                                        }`,
                                                    sx: {
                                                        height: '48px',
                                                        padding: '0 10px',
                                                        borderRadius: '8px',
                                                        background: '#F5F5F6',
                                                        border: '1px solid transparent',
                                                        width: '100%',
                                                        '&:focus-within': {
                                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            if (!props || !option?.asset) return null;
                                            const { key, ...rest } = props;
                                            const label = `${option.asset.assetNumber} - ${option.asset.name}`;
                                            const isRtl = direction === 'ltr';
                                            const childMarker = isRtl ? '↳' : '↲';
                                            return (
                                                <StyledListItem
                                                    key={key}
                                                    {...rest}
                                                    style={{
                                                        ...rest.style,
                                                        paddingInlineStart:
                                                            option.isChild && !isRtl ? 24 : undefined,
                                                        paddingInlineEnd:
                                                            option.isChild && isRtl ? 24 : undefined,
                                                    }}
                                                >
                                                    {option.isChild
                                                        ? isRtl
                                                            ? `${label} ${childMarker}`
                                                            : `${childMarker} ${label}`
                                                        : label}
                                                </StyledListItem>
                                            );
                                        }}
                                        PopperComponent={(props) => (
                                            <StyledPopper {...props} style={{ ...props.style, direction, zIndex: 1305 }} />
                                        )}
                                    />
                                </div>

                                {/* AssetGroup */}
                                <div className="edit-form-row-item" style={{ flex: 1 }}>
                                    <div
                                        className="edit-form-row-item-title"
                                        style={{ textAlign: 'right', width: '100%', marginBottom: '4px' }}
                                    >
                                        {t('FilterByAssetGroup')}
                                    </div>

                                    {/* 7) You could also add an “Add Group” button or logic here similarly */}
                                    <Autocomplete
                                        options={assetGroups || []}
                                        getOptionLabel={(option) => `${option.assetGroupId} - ${option.name}`}
                                        value={selectedGroup || null}
                                        onChange={(event, newVal) => setSelectedGroup(newVal)}
                                        isOptionEqualToValue={(option, val) => option.assetGroupId === val.assetGroupId}
                                        noOptionsText={<strong>{t('NoOptions')}</strong>}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('SelectAssetGroup')}
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
                                                        }`,
                                                    sx: {
                                                        height: '48px',
                                                        padding: '0 10px',
                                                        borderRadius: '8px',
                                                        background: '#F5F5F6',
                                                        border: '1px solid transparent',
                                                        width: '100%',
                                                        '&:focus-within': {
                                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <StyledListItem {...props}>
                                                {`${option.assetGroupId} - ${option.name}`}
                                            </StyledListItem>
                                        )}
                                        PopperComponent={(props) => (
                                            <StyledPopper {...props} style={{ ...props.style, direction, zIndex: 1305 }} />
                                        )}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px',
                                    marginTop: '16px',
                                }}
                            >
                                {/* Create New Asset */}
                                {!saleEquipment && (
                                    <button
                                        onClick={() => handleOpenAddAsset()}
                                        className="create-Invoice-selector-2-small"
                                    >
                                        <span>{t('CreateNewAsset')}</span>
                                        <AddCircleIcon fontSize="small" />
                                    </button>
                                )}
                            </div>

                            {/* Save button */}
                            <button
                                onClick={handleSaveSelection}
                                className="create-Invoice-selector-2-small"
                                disabled={isSaveDisabled}
                                style={{
                                    backgroundColor: isSaveDisabled ? '#ccc' : '#304FFF',
                                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                                    marginTop: '12px',
                                }}
                            >
                                <span>{t('Save')}</span>
                                <SaveIcon fontSize="small" />
                            </button>
                        </>
                    )}

                    {showAddAsset && (
                        <AddAssetInline
                            onClose={handleSaveNewAsset}
                            caseId={client.caseId}
                            token={token}
                            type={type}
                            itemId={itemId}
                            assets={unsoldAssets}
                            assetRelations={fetchedAssetRelations}
                            refreshRelations={refreshAssetRelations}
                            fullItem={fullItem}
                            client={client}
                            setShowAddAsset={setShowAddAsset}
                            user={user}
                            fetchEnums={fetchEnums}
                            assetGroups={assetGroups}
                            setShowAddGroupModal={setShowAddGroupModal}
                            editedItemVat={editedItemVat}
                            initialAdditionAsset={isAdditionToExistingAsset ? selectedAsset : null}
                        />
                    )}
                </>)}

                <EditAsset
                    caseId={client?.caseId}
                    open={editModalOpen}
                    onClose={closeEditModal}
                    asset={selectedAsset}
                    assetGroups={assetGroups}
                    assets={fetchedAssets}
                    assetRelations={fetchedAssetRelations}
                    refreshRelations={refreshAssetRelations}
                    refreshAssets={() => fetchEnums(user, token, client)}
                    onDelete={handleDelete}
                    onSave={() => fetchEnums(user, token, client)}
                    viewDocument={null}
                    token={token}
                    isDraggable={false}
                />


            </StyledContainer>
        </Draggable>
    );
}

export default ChooseAsset;
